import classNames from 'classnames';
import Quill from 'quill/core';
import React, { useMemo } from 'react';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import SVGIcon from '../components/SVGIcon';
import { iframeIcon } from './controls/editorIcons';
import type { DraggableBlot } from './ReorderGutterButton';
import styles from './ReorderSnapshot.module.scss';
import { VIDEO_SOURCES } from './utils/matchVideo';
import safeParseUrl from './utils/safeParseUrl';

const Parchment = Quill.import('parchment');

interface HTMLSnapshotProps {
  html: string;
  bordered?: boolean;
}

const HTMLSnapshot = ({ html, bordered }: HTMLSnapshotProps) => (
  <div className={classNames({ [styles.borderedContainer]: bordered })}>
    <div
      className="ql-editor reorder-dragging"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  </div>
);

interface ReorderSnapshotProps {
  blots: DraggableBlot[];
}

const ReorderSnapshot = ({ blots }: ReorderSnapshotProps) => {
  const node = useMemo(() => {
    if (blots[0] instanceof Parchment.LeafBlot) {
      const { blotName } = blots[0].statics;
      const value = blots[0].value()[blotName];

      switch (blotName) {
        case 'embed':
        case 'iframe':
        case 'video': {
          let icon = null;
          if (blotName === 'video') {
            const url = safeParseUrl(value);
            const source =
              url &&
              Object.values(VIDEO_SOURCES).find(
                ({ formatters: { embedUrl } }) => embedUrl.testHost(url.host),
              );
            if (source) {
              icon = <ExternalBrandLogo height={16} {...source.iconProps} />;
            }
          } else if (blotName === 'embed') {
            const [brand, product] = value.type.split('-');
            icon = (
              <ExternalBrandLogo height={16} brand={brand} product={product} />
            );
          } else {
            icon = <SVGIcon size={16}>{iframeIcon}</SVGIcon>;
          }
          return (
            <div
              className={classNames(
                styles.borderedContainer,
                styles.embedContainer,
              )}
            >
              {icon}
            </div>
          );
        }
        default:
          return <HTMLSnapshot html={blots[0].domNode.outerHTML} />;
      }
    }

    return (
      <HTMLSnapshot
        bordered
        html={`<ol>${blots
          .map(({ domNode }) => domNode.outerHTML)
          .join('')}</ol>`}
      />
    );
  }, [blots]);

  return node;
};

export default ReorderSnapshot;
